// src/api/index.ts
var ChuziApiError = class extends Error {
  constructor(status, body, message) {
    super(message ?? `chuzi-api ${status}`);
    this.status = status;
    this.body = body;
    this.name = "ChuziApiError";
  }
  status;
  body;
};
function isInsufficientCreditsError(err) {
  if (!(err instanceof ChuziApiError) || err.status !== 403) {
    return false;
  }
  const body = err.body;
  return typeof body?.error === "string" && /insufficient/i.test(body.error);
}
function insufficientCreditsPool(err) {
  if (!isInsufficientCreditsError(err)) {
    return null;
  }
  const body = err.body;
  const message = body.error ?? "";
  if (/watch/i.test(message)) return "watch";
  if (/create/i.test(message)) return "create";
  return null;
}
function buildQuery(query) {
  if (!query) return "";
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === void 0) continue;
    params.set(k, String(v));
  }
  const s = params.toString();
  return s ? `?${s}` : "";
}
function makeRequester(config) {
  const fetchFn = config.fetch ?? globalThis.fetch;
  const baseUrl = config.baseUrl.replace(/\/+$/, "");
  return async function request(method, path, opts = {}) {
    const headers = {
      Accept: "application/json"
    };
    if (config.client) headers["X-Chuzi-Client"] = config.client;
    if (config.getToken) {
      const token = await config.getToken();
      if (token) headers["Authorization"] = `Bearer ${token}`;
    }
    if (opts.body !== void 0) headers["Content-Type"] = "application/json";
    const res = await fetchFn(`${baseUrl}${path}${buildQuery(opts.query)}`, {
      method,
      headers,
      body: opts.body !== void 0 ? JSON.stringify(opts.body) : void 0,
      signal: opts.signal
    });
    if (res.status === 204) return void 0;
    let parsed = null;
    const text = await res.text();
    if (text.length > 0) {
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = text;
      }
    }
    if (!res.ok) {
      throw new ChuziApiError(res.status, parsed);
    }
    return parsed;
  };
}
function createChuziClient(config) {
  const request = makeRequester(config);
  return {
    auth: {
      login: (req) => request("POST", "/api/v1/auth/login", { body: req }),
      exchangeOidcCode: (req) => request("POST", "/api/v1/auth/oidc/exchange", { body: req }),
      requestMagicLink: (req) => request("POST", "/api/v1/auth/magic-link/request", { body: req }),
      verifyMagicLink: (req) => request("POST", "/api/v1/auth/magic-link/verify", { body: req }),
      usernameAvailability: (username, opts) => request("GET", "/api/v1/auth/username-availability", {
        signal: opts?.signal,
        query: { username }
      }),
      logout: () => request("POST", "/api/v1/auth/logout"),
      user: () => request("GET", "/api/v1/auth/user")
    },
    catalog: {
      index: (opts) => request("GET", "/api/v1/catalog", {
        signal: opts?.signal,
        query: {
          tag: opts?.tag,
          creator_id: opts?.creator_id
        }
      }),
      byCreator: (userId, opts) => request("GET", "/api/v1/catalog", {
        signal: opts?.signal,
        query: {
          creator_id: userId,
          tag: opts?.tag
        }
      })
    },
    config: {
      realms: (opts) => request("GET", "/api/v1/config/realms", {
        signal: opts?.signal,
        query: opts?.locale ? { locale: opts.locale } : void 0
      })
    },
    stories: {
      index: (opts) => request("GET", "/api/v1/stories", opts),
      show: (id, opts) => request("GET", `/api/v1/stories/${encodeURIComponent(id)}`, opts),
      mine: (opts) => request("GET", "/api/v1/stories/mine", opts),
      create: (req) => request("POST", "/api/v1/stories", { body: req }),
      update: (id, req) => request("PATCH", `/api/v1/stories/${encodeURIComponent(id)}`, { body: req }),
      destroy: (id) => request("DELETE", `/api/v1/stories/${encodeURIComponent(id)}`),
      jettison: (id) => request("POST", `/api/v1/stories/${encodeURIComponent(id)}/jettison`),
      unjettison: (id) => request("POST", `/api/v1/stories/${encodeURIComponent(id)}/unjettison`),
      purgeJettisoned: () => request("DELETE", "/api/v1/stories/jettisoned")
    },
    tags: {
      index: (opts) => request("GET", "/api/v1/tags", {
        signal: opts?.signal,
        query: { q: opts?.q }
      })
    },
    scenes: {
      index: (storyId, opts) => request("GET", "/api/v1/scenes", {
        signal: opts?.signal,
        query: { story_id: storyId }
      }),
      show: (id, opts) => request("GET", `/api/v1/scenes/${encodeURIComponent(id)}`, opts),
      create: (req) => request("POST", "/api/v1/scenes", { body: req }),
      update: (id, req) => request("PATCH", `/api/v1/scenes/${encodeURIComponent(id)}`, { body: req }),
      destroy: (id) => request("DELETE", `/api/v1/scenes/${encodeURIComponent(id)}`)
    },
    sceneActions: {
      index: (opts) => request("GET", "/api/v1/scene-actions", {
        signal: opts?.signal,
        query: opts?.sceneId ? { scene_id: opts.sceneId } : void 0
      }),
      show: (id, opts) => request("GET", `/api/v1/scene-actions/${encodeURIComponent(id)}`, opts),
      create: (req) => request("POST", "/api/v1/scene-actions", { body: req }),
      update: (id, req) => request("PATCH", `/api/v1/scene-actions/${encodeURIComponent(id)}`, {
        body: req
      }),
      destroy: (id) => request("DELETE", `/api/v1/scene-actions/${encodeURIComponent(id)}`)
    },
    media: {
      uploadUrl: (req) => request("POST", "/api/v1/media/upload-url", { body: req }),
      register: (req) => request("POST", "/api/v1/media/register", { body: req }),
      transcode: (req) => request("POST", "/api/v1/media/transcode", { body: req }),
      playUrl: (id, opts) => request("GET", `/api/v1/media/${encodeURIComponent(id)}/play`, opts),
      sourceUrl: (id, opts) => request("GET", `/api/v1/media/${encodeURIComponent(id)}/source-url`, opts)
    },
    watch: {
      sceneMap: (storyId, opts) => request("GET", `/api/v1/stories/${encodeURIComponent(storyId)}/scene-map`, opts),
      trackEngagement: (storyId, req) => request("POST", `/api/v1/stories/${encodeURIComponent(storyId)}/engagement`, { body: req }),
      saveBookmark: (storyId, req) => request("POST", `/api/v1/stories/${encodeURIComponent(storyId)}/bookmark`, { body: req }),
      listBookmarks: (storyId, opts) => request("GET", `/api/v1/stories/${encodeURIComponent(storyId)}/bookmarks`, opts)
    },
    user: {
      profile: (opts) => request("GET", "/api/v1/user/profile", opts),
      publicProfile: (userId, opts) => request("GET", `/api/v1/users/${encodeURIComponent(userId)}/public`, opts),
      updateRealm: (req) => request("PUT", "/api/v1/user/realm", { body: req }),
      updateLocale: (req) => request("PUT", "/api/v1/user/locale", { body: req }),
      updateProfile: (req) => request("PUT", "/api/v1/user/profile", { body: req }),
      usernameAvailability: (username, opts) => request("GET", "/api/v1/user/username-availability", {
        signal: opts?.signal,
        query: { username }
      })
    },
    credits: {
      balance: (opts) => request("GET", "/api/v1/credits/balance", opts),
      ledger: (opts) => request("GET", "/api/v1/credits/ledger", {
        signal: opts?.signal,
        query: opts?.pool ? { pool: opts.pool } : void 0
      }),
      packs: (opts) => request("GET", "/api/v1/credits/packs", opts),
      uploadCost: (fileSize, opts) => request("GET", "/api/v1/credits/upload-cost", {
        signal: opts?.signal,
        query: { file_size: fileSize }
      }),
      purchase: (req) => request("POST", "/api/v1/credits/purchase", { body: req }),
      purchaseAmount: (req) => request("POST", "/api/v1/credits/purchase-amount", { body: req }),
      unlockScene: (sceneId, opts) => request("POST", `/api/v1/scenes/${encodeURIComponent(sceneId)}/unlock`, opts)
    },
    payments: {
      list: (opts) => request("GET", "/api/v1/payments/methods", opts),
      store: (req) => request("POST", "/api/v1/payments/methods", { body: req }),
      remove: (paymentMethodId) => request("DELETE", `/api/v1/payments/methods/${encodeURIComponent(paymentMethodId)}`),
      setDefault: (paymentMethodId) => request(
        "POST",
        `/api/v1/payments/methods/${encodeURIComponent(paymentMethodId)}/default`
      )
    },
    admin: {
      credits: {
        grant: (req) => request("POST", "/api/v1/admin/credits/grant", { body: req })
      },
      systemStories: {
        create: (req) => request("POST", "/api/v1/admin/system-stories", { body: req }).then(
          (res) => res.data
        )
      }
    },
    ai: {
      generateImage: (req) => request("POST", "/api/v1/ai/generate-image", { body: req }),
      show: (id, opts) => request("GET", `/api/v1/ai/generations/${encodeURIComponent(id)}`, opts),
      accept: (id, req) => request("POST", `/api/v1/ai/generations/${encodeURIComponent(id)}/accept`, { body: req }),
      reject: (id, req) => request("POST", `/api/v1/ai/generations/${encodeURIComponent(id)}/reject`, { body: req }),
      storyStyle: (storyId, opts) => request("GET", `/api/v1/ai/stories/${encodeURIComponent(storyId)}/style`, opts),
      storyEstimate: (req) => request("POST", "/api/v1/ai/story/estimate", { body: req }),
      storyGenerate: (req) => request("POST", "/api/v1/ai/story/generate", { body: req }),
      sceneEstimate: (req) => request("POST", `/api/v1/ai/stories/${encodeURIComponent(req.story_id)}/scene/estimate`, {
        body: { brief: req.brief, mode: req.mode, scene_id: req.scene_id }
      }),
      sceneGenerate: (req) => request("POST", `/api/v1/ai/stories/${encodeURIComponent(req.story_id)}/scene/generate`, {
        body: { brief: req.brief, mode: req.mode, scene_id: req.scene_id }
      })
    },
    effects: {
      generate: (req) => request("POST", "/api/v1/effects/generate", { body: req }),
      marketplace: (opts) => {
        const query = {};
        if (opts?.tags) query.tags = opts.tags;
        if (opts?.type) query.type = opts.type;
        if (opts?.page) query.page = String(opts.page);
        return request("GET", "/api/v1/effects/marketplace", { ...opts, query });
      }
    }
  };
}

export { ChuziApiError, createChuziClient, insufficientCreditsPool, isInsufficientCreditsError };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map